import type { Clock, PersistedTimer, TimerPhase, TimerSnapshot } from './types';

const systemClock: Clock = {
  monotonicNow: () => performance.now(),
  wallNow: () => Date.now(),
};

export class Timer {
  private phase: TimerPhase = 'idle';
  private durationMs = 0;
  private frozenRemainingMs = 0;
  private deadlineMonotonicMs = 0;

  constructor(private readonly clock: Clock = systemClock) {}

  start(durationMs: number): void {
    if (!Number.isFinite(durationMs) || durationMs < 1_000) {
      throw new RangeError('計時長度至少需要 1 秒。');
    }
    this.durationMs = Math.round(durationMs);
    this.frozenRemainingMs = this.durationMs;
    this.deadlineMonotonicMs = this.clock.monotonicNow() + this.durationMs;
    this.phase = 'running';
  }

  pause(): void {
    if (this.phase !== 'running') return;
    this.frozenRemainingMs = this.currentRemainingMs();
    this.phase = 'paused';
  }

  resume(): void {
    if (this.phase !== 'paused') return;
    this.deadlineMonotonicMs = this.clock.monotonicNow() + this.frozenRemainingMs;
    this.phase = 'running';
  }

  togglePause(): void {
    if (this.phase === 'running') this.pause();
    else if (this.phase === 'paused') this.resume();
  }

  addTime(milliseconds: number): void {
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) return;
    const added = Math.round(milliseconds);
    this.durationMs += added;
    if (this.phase === 'running') this.deadlineMonotonicMs += added;
    else this.frozenRemainingMs += added;
  }

  reset(): void {
    this.phase = 'idle';
    this.frozenRemainingMs = 0;
    this.deadlineMonotonicMs = 0;
  }

  restore(saved: PersistedTimer): boolean {
    if (!Number.isFinite(saved.durationMs) || saved.durationMs < 1_000) return false;
    if (!Number.isFinite(saved.signedRemainingMs) || !Number.isFinite(saved.savedAtWallMs)) return false;

    const elapsedWhileClosed = saved.phase === 'running'
      ? Math.max(0, this.clock.wallNow() - saved.savedAtWallMs)
      : 0;
    const restoredRemaining = saved.signedRemainingMs - elapsedWhileClosed;

    this.durationMs = saved.durationMs;
    this.frozenRemainingMs = restoredRemaining;
    this.phase = saved.phase;
    this.deadlineMonotonicMs = this.clock.monotonicNow() + restoredRemaining;
    return true;
  }

  serialize(): PersistedTimer {
    const snapshot = this.snapshot();
    return {
      phase: snapshot.phase,
      durationMs: snapshot.durationMs,
      signedRemainingMs: snapshot.signedRemainingMs,
      savedAtWallMs: this.clock.wallNow(),
    };
  }

  snapshot(): TimerSnapshot {
    const signedRemainingMs = this.currentRemainingMs();
    const isOvertime = signedRemainingMs < 0;
    const displaySeconds = isOvertime
      ? Math.floor(Math.abs(signedRemainingMs) / 1_000)
      : Math.ceil(Math.max(0, signedRemainingMs) / 1_000);
    const progress = this.durationMs > 0
      ? Math.max(0, Math.min(1, signedRemainingMs / this.durationMs))
      : 1;

    return {
      phase: this.phase,
      durationMs: this.durationMs,
      signedRemainingMs,
      displaySeconds,
      isOvertime,
      progress,
    };
  }

  private currentRemainingMs(): number {
    if (this.phase !== 'running') return this.frozenRemainingMs;
    return this.deadlineMonotonicMs - this.clock.monotonicNow();
  }
}
